import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { DeviceKind, LayoutPresetId, Region, SourceConfig, View } from "@gooner/shared";
import { LAYOUT_PRESETS, makeId, presetRects, regionFromRect } from "@gooner/shared";

/** Best-effort device class from the browser UA, for a new monitor's deviceKind. */
export function detectDeviceKind(): DeviceKind {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  // Tablets first — Android tablets omit "Mobile", iPads report as such (or as Mac + touch).
  if (/iPad|Tablet|PlayBook|Silk|(?:Android(?!.*Mobile))/i.test(ua)) return "tablet";
  if (/iPhone|iPod|Android.*Mobile|Mobi|Windows Phone/i.test(ua)) return "phone";
  return "monitor";
}

/**
 * The layout a collection plays on when nobody picked one — the `/play/collection`
 * route without `?layout=`, the collection card's bare Play, and the starting pick of
 * the collection page's Play button (CollectionView). It MUST fit
 * `MAX_REGIONS_FREE`, because the play mints a temp view under the VIEWER's tier and
 * a signed-out viewer is the common case: this default was once grid-3x3, one region
 * over the cap of the day, and every default-layout play from a signed-out browser
 * died on the mint with a Gold upsell instead of playing.
 * The free cap is eight (2026-09-10; it was nine for a while, and grid-2x4 stayed the
 * default even then, deliberately: the mint is 16:9 (see `collectionView` below), and
 * eight tiles in two rows fill that shape where nine square ones leave the wall taller
 * than it is wide). Eight is exactly the cap, so this default is the biggest free wall.
 * (An explicit `?layout=` above the viewer's tier still gets the upsell — that one
 * is a deliberate ask for a Gold wall, not a default they never chose.)
 */
export const DEFAULT_PLAY_LAYOUT: LayoutPresetId = "grid-2x4";

/**
 * A random layout for the remote's tap-a-collection pick, where offering a full picker
 * would turn a one-thumb act into a form. Drawn from the SAME `LAYOUT_PRESETS` the
 * collection page's picker uses — one shared list, no curated subset to drift.
 *
 * `maxRegions` gates it to what the viewer's tier can actually play: a non-Gold tap must
 * not land an over-cap gold layout it cannot use. Callers pass the tier ceiling (the
 * remote already reads the gold settings); the Infinity default keeps a bare call working
 * and picks from everything.
 */
export function randomPlayLayout(maxRegions = Number.POSITIVE_INFINITY): LayoutPresetId {
  const allowed = LAYOUT_PRESETS.filter((p) => presetRects(p.id).length <= maxRegions);
  const pool = allowed.length ? allowed : LAYOUT_PRESETS;
  return pool[Math.floor(Math.random() * pool.length)]?.id ?? "grid-3x3";
}

/** Every region of a preset layout, all streaming one collection. */
function collectionRegions(collectionId: string, layout: LayoutPresetId): Region[] {
  return presetRects(layout).map((rect) => ({
    ...regionFromRect(rect),
    ...rect,
    id: makeId(),
    source: { kind: "playlist", playlistId: collectionId } as SourceConfig,
  }));
}

/** An in-memory view whose every region streams one collection. Never saved: it is what
 *  a signed-out "Play collection" renders through the real view panel. */
export function collectionView(
  collectionId: string,
  layout: LayoutPresetId,
  name = "Collection",
): View {
  const now = Date.now();
  return {
    id: makeId(),
    name,
    aspect: { w: 16, h: 9 },
    regions: collectionRegions(collectionId, layout),
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Play a collection: one route, signed in or out.
 *
 * There used to be two. A signed-in user got a real throwaway Monitor added to their
 * "scratch cave" and was sent to `/view/:caveId/:monitorId`; everyone else got an
 * in-memory view at `/play/collection/:id`. Caves are gone, and the second path became
 * the only one when it started minting a real temporary view on a real device — so a
 * signed-in operator now gets everything the cave path was for (an addressable screen, a
 * row in the rail, a goon session) without a cave to hang it off.
 */
export function usePlayCollection() {
  const navigate = useNavigate();
  return useCallback(
    (collection: { id: string; name: string }, layout: LayoutPresetId = DEFAULT_PLAY_LAYOUT) => {
      navigate(`/play/collection/${collection.id}?layout=${layout}`);
    },
    [navigate],
  );
}
